import React, { useEffect, useState } from "react";
import PieChart, {
  Legend,
  Series,
  Tooltip,
  Size,
} from "devextreme-react/pie-chart";
import { RingChart } from "./RingChart";
import { baseRequest } from "../../fetch/basicThunk";
import { useDispatch, useSelector } from "react-redux";
import {
  QUERY_GET_AGENT_KPI,
  QUERY_GET_CASE_KPI,
} from "../../fetch/constantQueries";
import { KpiTable } from "./KpiTable";
import { Column } from "devextreme-react/data-grid";
import { openModal } from "../../redux/modal/modalSlice";
import { PanelHeader } from "./PanelHeader";
import CustomTagBox from "../common/CustomTagBox";
import {
  selectUserRoles,
  selectUserSuperAdmin,
} from "../../redux/authentication/selectors";
import { ADMIN, MANAGER } from "../../Constantes/userRoles";
import { isUserInRole } from "../../commonFunctions/commonFunctions";
import { getServices } from "../../redux/services/thunks";
import { selectServices } from "../../redux/services/selectors";
import { Button } from "@material-ui/core";
import { TextBox } from "devextreme-react";
import { useTranslation } from "react-i18next";

function Dashboard() {
  const { t } = useTranslation();
  const [caseData, setCaseData] = useState({
    casesByCategory: [
      { type: t("Categorized"), value: "-" },
      { type: t("Ongoing"), value: "-" },
    ],
    casesByTaxonomy: [],
    totalCases: "-",
    taxonomyCount: "-",
    uncategorizedCases: [],
  });
  const [agentData, setAgentData] = useState({
    agentsByAvaliability: [],
    agentsByAllocation: [
      { type: t("Assigned"), value: "-" },
      { type: t("Unassigned"), value: "-" },
    ],
    activeAgentsCount: "-",
    avaliableAgents: [],
    unavaliableAgents: [],
    avaliableAgentsCount: "-",
    unavaliableAgentsCount: "-",
  });
  const [loadingCaseData, setLoadingCaseData] = useState(true);
  const [loadingAgentData, setLoadingAgentData] = useState(true);
  const userRoles = useSelector(selectUserRoles);
  const userSuperAdmin = useSelector(selectUserSuperAdmin);
  const isAdmin = isUserInRole(ADMIN, userRoles, userSuperAdmin);
  const allServices = useSelector(selectServices);
  const [userServices, setUserServices] = useState();
  const [selectedServices, setSelectedServices] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    getCaseKpiData();
    getAgentsKpiData();
    dispatch(getServices());
  }, []);

  useEffect(() => {
    if (allServices && userRoles) {
      if (isAdmin) {
        setUserServices(allServices);
        return;
      }

      if (isUserInRole(MANAGER, userRoles, userSuperAdmin)) {
        const roleManagerServices = userRoles.find(
          (r) => r.role.roleName === MANAGER
        ).services;
        const avaliableServices =
          roleManagerServices.length === 0 ? allServices : roleManagerServices;
        setUserServices(avaliableServices);
      }
    }
  }, [allServices, userRoles]);

  function customizeTooltip(arg) {
    return {
      text: `${
        arg.argumentText.trim() === "" ? "SIN TAXONOMIZAR" : arg.argumentText
      } : ${arg.value}`,
    };
  }

  async function getCaseKpiData() {
    const filterByService = selectedServices.length > 0;
    const serviceIds = selectedServices.join(",");

    try {
      const data = await baseRequest(
        filterByService ? { query: `?services=${serviceIds}` } : null,
        dispatch,
        QUERY_GET_CASE_KPI,
        "GET"
      );
      const casesByCategory = [
        { type: t("Categorized"), value: data.categorizedCases },
        { type: t("Ongoing"), value: data.uncategorizedCasesCount },
      ];
      const casesByTaxonomy = [...data.taxonomies];
      const transformedData = {
        casesByCategory,
        casesByTaxonomy,
        uncategorizedCasesCount: data.uncategorizedCasesCount,
        uncategorizedCases: data.uncategorizedCases,
        totalCases: data.totalCases,
        cases: data.cases,
        taxonomyCount: data.taxonomyCount,
      };

      setCaseData(transformedData);
      setLoadingCaseData(false);
    } catch (error) {
      dispatch(
        openModal({
          type: "warning",
          message: "No se han podido obtener métricas de casos",
        })
      );
    }
  }

  async function getAgentsKpiData() {
    const filterByService = selectedServices.length > 0;
    const serviceIds = selectedServices.join(",");

    try {
      const data = await baseRequest(
        filterByService ? { query: `?services=${serviceIds}` } : null,
        dispatch,
        QUERY_GET_AGENT_KPI,
        "GET"
      );
      const agentsByAvaliability = [
        { type: "Disponibles", value: data.avaliableAgentsCount },
        { type: "No disponibles", value: data.unavaliableAgentsCount },
      ];
      const agentsByAllocation = [
        { type: t("Assigned"), value: data.allocatedAgents },
        { type: t("Unassigned"), value: data.unAllocatedAgents },
      ];
      const transformedData = {
        agentsByAvaliability,
        agentsByAllocation,
        avaliableAgents: data.avaliableAgents,
        unavaliableAgents: data.unavaliableAgents,
        avaliableAgentsCount: data.avaliableAgentsCount,
        unavaliableAgentsCount: data.unavaliableAgentsCount,
        activeAgents: data.activeAgents,
      };

      setAgentData(transformedData);
      setLoadingAgentData(false);
    } catch (error) {
      dispatch(
        openModal({
          type: "warning",
          message: "No se han podido obtener métricas de agentes",
        })
      );
    }
  }

  const caseColumns = [
    <Column
      key={"case-1"}
      caption={t("Agent")}
      width={"10%"}
      dataField={"agent"}
      dataType={"string"}
    />,
    <Column
      key={"case-2"}
      caption={t("CaseId")}
      width={"15%"}
      dataField={"caseId"}
      dataType={"string"}
    />,
    <Column
      key={"case-3"}
      caption={t("Customer")}
      dataField={"customerName"}
      dataType={"string"}
    />,
    <Column
      key={"case-4"}
      caption={t("CreatedAt")}
      format={"dd/MM/yyyy HH:mm"}
      width={"15%"}
      dataField={"creationDate"}
      dataType={"datetime"}
    />,
    <Column
      key={"case-5"}
      caption={t("ClosedAt")}
      format={"dd/MM/yyyy HH:mm"}
      width={"15%"}
      dataField={"closeDate"}
      dataType={"datetime"}
    />,
  ];

  const agentColumns = [
    <Column
      key={"agent-1"}
      caption={"Agente"}
      calculateDisplayValue={(r) => `${r.userId} - ${r.userName}`}
      dataType={"string"}
    />,
  ];

  function handleChangeServices(newServices) {
    setSelectedServices(newServices);
  }

  function handleSearch() {
    getCaseKpiData();
    getAgentsKpiData();
  }

  return (
    <div className="dashboard-container">
      <div
        style={{
          gridColumnStart: "1",
          gridColumnEnd: "3",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ width: "25rem" }}>
          {userServices && userServices?.length === 1 ? (
            <TextBox
              label={t("Services")}
              labelMode="floating"
              readOnly={true}
              value={userServices[0]?.serviceName}
            />
          ) : (
            <CustomTagBox
              label={t("Services")}
              dataSource={userServices}
              showClearButton={false}
              defaultValue={selectedServices}
              displayExpr={"serviceName"}
              valueExpr={"serviceId"}
              onValueChanged={(e) => {
                handleChangeServices(e.value);
              }}
              searchEnabled={true}
              hideSelectedItems
            />
          )}
        </span>
        <Button onClick={handleSearch} variant="contained" color="primary">
          {t("ApplyFilters")}
        </Button>
      </div>
      <div className="dashboard-panel">
        <PanelHeader title={`${t("OpenCases")}:`} value={caseData.totalCases} />
        <div className="dashboard-panel-chart-container">
          <RingChart
            datasource={caseData.casesByCategory}
            palette={["#116864", "#66B8B2"]}
            isLoading={loadingCaseData}
          />
          <div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                rowGap: "1.5rem",
              }}
            >
              <PieChart
                id="pie"
                loadingIndicator={{ show: loadingCaseData, text: "Cargando" }}
                type="doughnut"
                palette={["#001D33", "#004975", "#003253", "#00629A"]}
                dataSource={caseData.casesByTaxonomy}
                startAngle={35}
                innerRadius={0.65}
              >
                <Series
                  argumentField="taxonomyDescription"
                  valueField={"quantity"}
                  hoverStyle={{ hatching: { opacity: 100 } }}
                />
                <Legend visible={false} />
                <Tooltip
                  enabled={true}
                  toolt
                  customizeTooltip={customizeTooltip}
                />
                <Size height={200} width={250} />
              </PieChart>
              <div style={{ display: "flex", columnGap: "0.25rem" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateRows: "1fr 1fr",
                    textAlign: "center",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ fontWeight: 700 }}>
                    {caseData.taxonomyCount}
                  </div>
                  <div>{`${t("Taxonomies")} (${t("SelectToView")})`}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="dashboard-panel">
        <PanelHeader
          title={`${t("OngoingCases")}:`}
          value={caseData.uncategorizedCasesCount}
        />
        <div style={{ padding: "0 2rem" }}>
          <KpiTable
            dataSource={caseData.uncategorizedCases}
            showHeaders={true}
            columns={caseColumns}
            isLoadingData={loadingCaseData}
          ></KpiTable>
        </div>
      </div>
      <div className="dashboard-panel">
        <PanelHeader
          title={`${t("AvailableAgents")}:`}
          value={agentData.avaliableAgentsCount}
        />
        <div className="dashboard-panel-chart-container">
          <RingChart
            isLoading={loadingAgentData}
            datasource={agentData.agentsByAllocation}
            palette={["#006C3A", "#5EBD82"]}
          />
        </div>
      </div>
      <div className="dashboard-panel" style={{ padding: "0 1rem" }}>
        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              columnGap: "3rem",
            }}
          >
            <div>
              <PanelHeader
                title={`${t("AvailableAgents")}:`}
                value={agentData.avaliableAgentsCount}
              />
              <div>
                <KpiTable
                  dataSource={agentData.avaliableAgents}
                  showHeaders={false}
                  columns={agentColumns}
                  isLoadingData={loadingAgentData}
                />
              </div>
            </div>
            <div>
              <PanelHeader
                title={`${t("UnavailableAgents")}:`}
                value={agentData.unavaliableAgentsCount}
              />
              <div>
                <KpiTable
                  dataSource={agentData.unavaliableAgents}
                  showHeaders={false}
                  columns={agentColumns}
                  isLoadingData={loadingAgentData}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { Dashboard };
